import {
  type EventParticipant,
  type EventParticipantRole,
  type EventParticipantRsvp,
  type Pagination,
  createEventParticipant,
} from 'models';
import { useInfiniteQuery } from 'query';
import type { PaginationData } from 'common/services';
import { getEventParticipants } from '../../services';
import { getEventParticipantsInfiniteQueryKey } from '../keys';
import { useGetEventParticipantsParameters } from './hooks';

export type EventParticipantsFilter = {
  eventId: string;
  role?: EventParticipantRole;
  rsvp?: EventParticipantRsvp;
  search?: string;
};

export type EventParticipantsPageParameter = {
  skip: number;
};

type EventParticipantsQueryParams = {
  filter: EventParticipantsFilter;
  options?: { enabled?: boolean };
  pagination: Omit<Pagination, 'position'>;
};

export const useEventParticipantsInfiniteQuery = ({ filter, options, pagination }: EventParticipantsQueryParams) => {
  const getEventParticipantsParameters = useGetEventParticipantsParameters();

  return useInfiniteQuery<PaginationData<EventParticipant>, EventParticipantsPageParameter>({
    async queryFn({ pageParam }) {
      const { data: eventParticipants } = await getEventParticipants(
        getEventParticipantsParameters({ filter, pageParam, pagination }),
      );

      return {
        data: eventParticipants.data.map((participant) => createEventParticipant(participant)),
        limit: eventParticipants.limit,
        skip: eventParticipants.skip,
        total: eventParticipants.total,
      };
    },
    initialPageParam: { skip: 0 },
    getNextPageParam: (lastPage) =>
      lastPage.skip + lastPage.data.length < lastPage.total ? { skip: lastPage.skip + lastPage.limit } : undefined,
    queryKey: getEventParticipantsInfiniteQueryKey({ filter, pagination }),
    ...options,
  });
};
